import {
  Badge as MantineBadge,
  Box,
  Group,
  Stack,
  Text as MantineText,
  Title,
  Code,
} from '@mantine/core';
import { Badge, Table, Text, Card } from '../../components/ui';

const sampleData = [
  { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'Active' },
  { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Editor', status: 'Active' },
  { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'Viewer', status: 'Inactive' },
  { id: 4, name: 'Alice Brown', email: 'alice@example.com', role: 'Editor', status: 'Active' },
  { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', role: 'Admin', status: 'Pending' },
];

const projectData = [
  { name: 'Website Redesign', progress: 75, priority: 'High', deadline: '2024-03-15' },
  { name: 'Mobile App', progress: 45, priority: 'Medium', deadline: '2024-04-01' },
  { name: 'API Integration', progress: 90, priority: 'Low', deadline: '2024-02-28' },
  { name: 'Database Migration', progress: 30, priority: 'High', deadline: '2024-03-30' },
];

export default function TablesPage() {
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Table Components</Title>
        <MantineText c="dimmed" size="sm">
          Theme-aware tables with various styles, configurations, and examples
        </MantineText>
      </Stack>

      {/* Overview */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Table Components Overview</Title>
          <MantineText size="sm" c="dimmed">
            This page demonstrates all available table variants including default, striped, hoverable, and bordered styles with integrated components.
          </MantineText>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Available Table Variants:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • Default - Clean, minimal table styling
              <br />
              • Striped - Alternating row background colors
              <br />
              • Hoverable - Highlight rows on hover
              <br />
              • Bordered - Table with cell borders
              <br />
              • Integration with Badge, Progress, and other components
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Default Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Default Table</Title>
          <Table tableVariant="default">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sampleData.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td>{row.role}</Table.Td>
                  <Table.Td>
                    <MantineBadge
                      color={
                        row.status === 'Active'
                          ? 'green'
                          : row.status === 'Pending'
                            ? 'yellow'
                            : 'gray'
                      }
                      variant="light"
                    >
                      {row.status}
                    </MantineBadge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block data-language="tsx">
{`<Table tableVariant="default">
  <Table.Thead>
    <Table.Tr>
      <Table.Th>ID</Table.Th>
      <Table.Th>Name</Table.Th>
      <Table.Th>Email</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {data.map((row) => (
      <Table.Tr key={row.id}>
        <Table.Td>{row.id}</Table.Td>
        <Table.Td>{row.name}</Table.Td>
        <Table.Td>{row.email}</Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Striped Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Striped Table</Title>
          <MantineText size="sm" c="dimmed">
            Alternating row colors for improved readability
          </MantineText>

          <Table tableVariant="striped">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sampleData.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td>{row.role}</Table.Td>
                  <Table.Td>{row.status}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block data-language="tsx">
{`<Table tableVariant="striped">
  <Table.Thead>
    <Table.Tr>
      <Table.Th>ID</Table.Th>
      <Table.Th>Name</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {data.map((row) => (
      <Table.Tr key={row.id}>
        <Table.Td>{row.id}</Table.Td>
        <Table.Td>{row.name}</Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Hoverable Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Hoverable Table</Title>
          <MantineText size="sm" c="dimmed">
            Rows highlight on hover - perfect for interactive tables
          </MantineText>

          <Table tableVariant="hoverable">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sampleData.map((row) => (
                <Table.Tr key={row.id} style={{ cursor: 'pointer' }}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td>{row.role}</Table.Td>
                  <Table.Td>{row.status}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block data-language="tsx">
{`<Table tableVariant="hoverable">
  <Table.Thead>
    <Table.Tr>
      <Table.Th>Name</Table.Th>
      <Table.Th>Email</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {data.map((row) => (
      <Table.Tr key={row.id} style={{ cursor: 'pointer' }}>
        <Table.Td>{row.name}</Table.Td>
        <Table.Td>{row.email}</Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Bordered Table */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Bordered Table</Title>
          <MantineText size="sm" c="dimmed">
            Table with visible cell borders
          </MantineText>

          <Table tableVariant="bordered">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sampleData.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.id}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.email}</Table.Td>
                  <Table.Td>{row.role}</Table.Td>
                  <Table.Td>{row.status}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block data-language="tsx">
{`<Table tableVariant="bordered">
  <Table.Thead>
    <Table.Tr>
      <Table.Th>ID</Table.Th>
      <Table.Th>Name</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {data.map((row) => (
      <Table.Tr key={row.id}>
        <Table.Td>{row.id}</Table.Td>
        <Table.Td>{row.name}</Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Table with Gradient Badges */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Table with Gradient Badges & Progress</Title>
          <MantineText size="sm" c="dimmed">
            Combining tables with other UI components like badges and progress bars
          </MantineText>

          <Table tableVariant="striped">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Project</Table.Th>
                <Table.Th>Progress</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Deadline</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {projectData.map((project) => (
                <Table.Tr key={project.name}>
                  <Table.Td fw={500}>{project.name}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Box
                        style={{
                          width: 60,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'var(--mantine-color-gray-3)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          style={{
                            width: `${project.progress}%`,
                            height: '100%',
                            background: `linear-gradient(90deg, var(--mantine-primary-color-filled), var(--mantine-color-blue-4))`,
                          }}
                        />
                      </Box>
                      <MantineText size="sm">{project.progress}%</MantineText>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      gradientVariant={
                        project.priority === 'High'
                          ? 'danger'
                          : project.priority === 'Medium'
                            ? 'warning'
                            : 'success'
                      }
                      size="sm"
                    >
                      {project.priority}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{project.deadline}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block data-language="tsx">
{`<Table tableVariant="striped">
  <Table.Thead>
    <Table.Tr>
      <Table.Th>Project</Table.Th>
      <Table.Th>Priority</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {projectData.map((project) => (
      <Table.Tr key={project.name}>
        <Table.Td fw={500}>{project.name}</Table.Td>
        <Table.Td>
          <Badge
            gradientVariant={
              project.priority === 'High' ? 'danger' : 'success'
            }
            size="sm"
          >
            {project.priority}
          </Badge>
        </Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Key Props */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Table Variants & Props</Title>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Available tableVariant values:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • <code>default</code> - Clean, minimal styling
              <br />
              • <code>striped</code> - Alternating row colors
              <br />
              • <code>hoverable</code> - Highlight on hover
              <br />
              • <code>bordered</code> - With cell borders
            </MantineText>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Table Components:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • <code>Table.Thead</code> - Table header section
              <br />
              • <code>Table.Tbody</code> - Table body section
              <br />
              • <code>Table.Tr</code> - Table row
              <br />
              • <code>Table.Th</code> - Table header cell
              <br />
              • <code>Table.Td</code> - Table data cell
            </MantineText>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
